import { ApiService } from "./ApiService.js";
import { indicatorService } from "./IndicatorService.js";
import { tokenUtils } from "../../security/securityUtils.js";

// Re-export ApiService and IndicatorService for backward compatibility
export { ApiService };
export { indicatorService };

// Authentication service
export class AuthService extends ApiService {
  constructor() {
    super("/api/");
  }

  async login(credentials) {
    const response = await this.post("login/", credentials);
    return response.data;
  }

  async logout(refresh) {
    try {
      await this.post("auth/logout/", { refresh });
    } catch (error) {
      console.error(
        "authService.logout - error:",
        error.response?.data || error.message
      ); // <-- log error
      throw error;
    }
  }

  async refreshToken(refresh) {
    const response = await this.post("/jwt/refresh/", { refresh });
    return response.data;
  }

  async getProfile() {
    const response = await this.get("auth/profile");
    return response.data;
  }

  async updateProfile(profileData) {
    const response = await this.put("auth/profile", profileData);
    return response.data;
  }

  async changePasswordRequest(email) {
    const response = await this.post("password/reset/", email);
    return response.data;
  }
  async changePassword(payload) {
    const response = await this.post("password/reset/confirm/", payload);
    return response.data;
  }

  async getMe() {
    const res = await this.get("auth/users/me/");
    return res.data;
  }
}

// User management service
export class UserService extends ApiService {
  constructor() {
    super("/api/");
  }

  async getUsers(params = {}) {
    const requestParams = { ...params };

    if (requestParams.fromAddTeamModal) {
      requestParams.full_name = requestParams.search || "";
      delete requestParams.search;
      delete requestParams.fromAddTeamModal;
    }

    const response = await this.get("users/", { params: requestParams });
    return response.data;
  }

  async getUser(id) {
    const response = await this.get(`/${id}`);
    return response.data;
  }

  async createUser(userData) {
    const response = await this.post("invite/", userData);
    return response.data;
  }

  async updateUser(userData) {
    const response = await this.put(`users/${userData.id}/update/`, userData);
    return response.data;
  }

  async deleteUser(id) {
    const response = await this.patch(`users/${id}/delete/`);
    return response.data;
  }

  async toggleActiveStatus(data) {
    const response = await this.patch(`users/${data.id}/update/`, data);
    return response.data;
  }

  async inviteUser(inviteData) {
    const response = await this.post("/invite", inviteData);
    return response.data;
  }
  async reInvite(email) {
    const response = await this.post("invite/resend/", { email: email });
    return response.data;
  }

  async revokeAccess(id) {
    const response = await this.post(`/${id}/revoke-access`);
    return response.data;
  }
}
export class ProjectService extends ApiService {
  constructor() {
    super("/api/");
  }

  async getProjects(params = {}) {
    const response = await this.get("projects/", { params });
    return response.data;
  }

  async getProject(id) {
    const response = await this.get(`projects/${id}/`);
    return response.data;
  }

  async createProject(projectData) {
    const response = await this.post("projects/", projectData);
    return response.data;
  }

  async updateProject(projectData) {
    const response = await this.put(`projects/${projectData.id}/`, projectData);
    return response.data;
  }

  async archiveProject(data) {
    const response = await this.patch(`projects/${data.id}/`, {
      status_project: "archived",
    });
    return response.data;
  }
  async unarchiveProject(data) {
    const response = await this.patch(`projects/${data.id}/`, {
      status_project: "in_progress",
    });
    return response.data;
  }
  async changeProjectStatus(id, newStatus) {
    const response = await this.patch(`projects/${id}/`, {
      status_project: newStatus,
    });
    return response.data;
  }
  async inviteUsers(projectId, emails) {
    const response = await this.post(
      `projects/${projectId}/invite_users/`,
      emails
    );
    return response.data;
  }

  async removeMember(projectId, email) {
    const response = await this.post(
      `projects/${projectId}/remove_user/`,
      email
    );
    return response.data;
  }

  async getProjectHistory(projectId, params) {
    const response = await this.get(`projects/${projectId}/history/`, {
      params,
    });
    return response.data;
  }

  async getBulkStats(projectId) {
    const response = await this.get(
      `projects/${projectId}/activities/bulk-stats/`
    );
    return response.data;
  }
}

//Outcome CRUD service
export class OutcomeService extends ApiService {
  constructor() {
    super("/api/");
  }

  async getOutcomes(params = {}, projectId) {
    const queryParams = { ...params, projectId };
    const response = await this.get(`projects/${projectId}/outcomes/`, {
      params: queryParams,
    });
    return response.data;
  }

  async getOutcome(id) {
    const response = await this.get(`outcomes/${id}/`);
    return response.data;
  }

  async updateOutcomeRag(id, rag, rag_comment = null) {
    const payload = { rag };
    if (rag_comment) {
      payload.rag_comment = rag_comment;
    }
    const response = await this.patch(`outcomes/${id}/`, payload);
    return response.data;
  }

  async createOutcome(outcomeData) {
    const response = await this.post("outcomes/", outcomeData);
    return response.data;
  }

  async updateOutcome(outcomeData) {
    const response = await this.patch(
      `outcomes/${outcomeData.id}/`,
      outcomeData
    );
    return response.data;
  }

  async deleteOutcome(id) {
    const response = await this.patch(`outcomes/${id}/soft-delete/`);
    return response.data;
  }

  async restoreOutcome(id) {
    const response = await this.patch(`outcomes/${id}/restore/`);
    return response.data;
  }

  async getDeletedOutcomes(projectId, params = {}) {
    const response = await this.get(`projects/${projectId}/outcomes/deleted/`, { params });
    return response.data;
  }

  async addLinkageOCM(outcome, outputIds) {
    const existingIds = Array.isArray(outcome.output_ids)
      ? outcome.output_ids
      : [];
    const allIds = [...existingIds, ...outputIds];
    const response = await this.patch(`outcomes/${outcome.id}/`, {
      output_ids: allIds,
    });
    return response.data;
  }

  async getCount(projectId) {
    const response = await this.get(`projects/${projectId}/outcomes/count/`);
    return response.data.count;
  }

  async getAvailableOutputs(outcomeId, params = {}) {
    const response = await this.get(
      `outcomes/${outcomeId}/available-outputs/`,
      {
        params,
      }
    );
    return response.data;
  }

  async removeLinkageOCM(outcome, outputId) {
    console.log("%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%");
    console.log(
      "Removing linkage OCM for outcome:",
      outcome,
      "and outputId:",
      outputId
    );
    console.log("%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%");
    const existingIds = Array.isArray(outcome.output_ids)
      ? outcome.output_ids
      : [];
    const allIds = existingIds.filter((id) => id !== outputId);
    const response = await this.patch(`outcomes/${outcome.id}/`, {
      output_ids: allIds,
    });
    return response.data;
  }
}

// Document service
export class DocumentService extends ApiService {
  constructor() {
    super("/api/documents");
  }

  async getDocuments(linkedType, linkedId, params = {}) {
    const token = tokenUtils.getAccessToken();
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: params,
    };
    const response = await this.get(`/list/${linkedType}/${linkedId}/`, config);
    if (Array.isArray(response.data)) return response.data;
    if (response.data?.results) return response.data.results;
    return [];
  }

  async uploadDocuments(
    files,
    linkedType,
    linkedId,
    onProgress = null,
    signal = null
  ) {
    const formData = new FormData();

    // Append all files
    files.forEach((file) => {
      formData.append("files", file);
    });

    formData.append("entity_type", linkedType);
    formData.append("entity_id", linkedId);

    const token = tokenUtils.getAccessToken();
    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
      timeout: 180000, // 3 minutes timeout for file uploads (up to 50MB)
      signal: signal, // Add abort signal for cancellation support
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        if (progressEvent.lengthComputable && progressEvent.total > 0) {
          const total = progressEvent.total;
          const loaded = progressEvent.loaded;
          // Calculate actual upload progress as percentage (0-100%)
          const uploadProgress = Math.round((loaded / total) * 100);

          if (typeof onProgress === "function") {
            onProgress(uploadProgress);
          }
        }
      };
    }

    const response = await this.post("/upload/", formData, config);
    return response.data;
  }

  async deleteDocument(id) {
    const token = tokenUtils.getAccessToken();
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await this.delete(`/${id}/`, config);
    return response.data;
  }

  async downloadDocument(id, filename) {
    const token = tokenUtils.getAccessToken();
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: "blob",
      timeout: 120000, // 2 minutes timeout for document downloads
    };
    const response = await this.get(`/${id}/download/`, config);

    // Create blob URL and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;

    // Use the filename passed from the component, fallback to Content-Disposition header or default
    let downloadFilename = filename || "document";

    if (!filename) {
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          downloadFilename = filenameMatch[1];
        }
      }
    }

    link.setAttribute("download", downloadFilename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return response.data;
  }
}

export class OutputService extends ApiService {
  constructor() {
    super("/api/");
  }

  async createOutput(data) {
    const response = await this.post("outputs/", data);
    return response.data;
  }

  async getOutputs(params = {}) {
    const response = await this.get("outputs/", { params });
    return response.data;
  }

  async getOutput(id) {
    const response = await this.get(`outputs/${id}/`);
    return response.data;
  }

  async updateOutput(id, data) {
    const response = await this.patch(`outputs/${id}/`, data);
    return response.data;
  }

  async deleteOutput(id) {
    const response = await this.patch(`outputs/${id}/soft-delete/`);
    return response.data;
  }

  async restoreOutput(id) {
    const response = await this.patch(`outputs/${id}/restore/`);
    return response.data;
  }

  async getDeletedOutputs(projectId, params = {}) {
    const response = await this.get(`projects/${projectId}/outputs/deleted/`, { params });
    return response.data;
  }
  async addLinkageOPT(output, outcomeIds) {
    const existingIds = Array.isArray(output.outcome_ids)
      ? output.outcome_ids
      : [];
    const allIds = [...existingIds, ...outcomeIds];
    const response = await this.patch(`outputs/${output.id}/`, {
      outcome_ids: allIds,
    });
    return response.data;
  }

  async removeLinkageOPT(output, outcomeId) {
    const existingIds = Array.isArray(output.outcome_ids)
      ? output.outcome_ids
      : [];
    const allIds = existingIds.filter((id) => id !== outcomeId);
    const response = await this.patch(`outputs/${output.id}/`, {
      outcome_ids: allIds,
    });
    return response.data;
  }

  async getCount(projectId) {
    const response = await this.get(`projects/${projectId}/outputs/count/`);
    return response.data.count;
  }

  async getAvailableOutcomes(outputId, params = {}) {
    const response = await this.get(`outputs/${outputId}/available-outcomes/`, {
      params,
    });
    return response.data;
  }
}

// Activity service
export class ActivityService extends ApiService {
  constructor() {
    super("/api/");
  }

  async createActivity(data) {
    // Use project-specific endpoint for creating activities
    const projectId = data.project;
    // Transform risk to risk_level for backend
    const { risk, ...rest } = data;
    const payload = { ...rest, risk_level: risk };
    const response = await this.post(
      `projects/${projectId}/activities/`,
      payload
    );
    return response.data;
  }

 async getActivities(params = {}) {
  // Filter out null and undefined values from params
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value != null)
  );
  
  // If projectId is provided, use project-specific endpoint
  if (cleanParams.project_id) {
    const projectId = cleanParams.project_id;
    const { project_id, ...restParams } = cleanParams;
    const response = await this.get(`projects/${projectId}/activities/`, { 
      params: restParams 
    });
    return response.data;
  }
  
  // Fallback to generic endpoint
  const response = await this.get("activities/", { params: cleanParams });
  return response.data;
}

  async getDeletedActivities(projectId, params = {}) {
    const response = await this.get(`projects/${projectId}/activities/deleted/`, { params });
    return response.data;
  }

  async getActivity(id) {
    const response = await this.get(`activities/${id}/`);
    return response.data;
  }

  async updateActivity(id, data) {
    // Transform risk to risk_level for backend
    const { risk, ...rest } = data;
    const payload = { ...rest, risk_level: risk };
    const response = await this.patch(`activities/${id}/`, payload);
    return response.data;
  }

  async deleteActivity(id) {
    const response = await this.patch(`activities/${id}/soft-delete/`);
    return response.data;
  }

  async restoreActivity(id) {
    const response = await this.patch(`activities/${id}/restore/`);
    return response.data;
  }

  async getCount(projectId) {
    const response = await this.get(`projects/${projectId}/activities/count/`);
    return response.data.count;
  }

  async getActivityStats(activityId) {
    const response = await this.get(`activities/${activityId}/stats/`);
    return response.data;
  }

  async getAvailableOutputs(activityId, params = {}) {
    const response = await this.get(
      `activities/${activityId}/available-outputs/`,
      {
        params,
      }
    );
    return response.data;
  }
  async addLinkageATO(activity, outputIds) {
    // console.log("Adding linkage ATO for activity:", activity, "and outputIds:", outputIds);
    const existingIds = Array.isArray(activity.outputs)
      ? activity.outputs.map(o => o.id)
      : [];
    const allIds = [...existingIds, ...outputIds];
    console.log("All output IDs after addition:", allIds);
    const response = await this.patch(`activities/${activity.id}/`, {
      output_ids: allIds,
    });
    return response.data;
  }
  async removeLinkageATO(activity, output_id) {
    const response = await this.patch(
      `activities/${activity.id}/unlink-output/`,
      {
        output_ids: [output_id],
      }
    );
    return response.data;
  }
}

// Expense service
export class ExpenseService extends ApiService {
  constructor() {
    super("/api/");
  }

  async createExpense(data) {
    const projectId = data.project;
    const response = await this.post(`projects/${projectId}/expenses/`, data);
    return response.data;
  }

  async getExpenses(params = {}) {
    // If projectId is provided, use project-specific endpoint
    if (params.project_id) {
      const projectId = params.project_id;
      const { project_id, ...restParams } = params;
      const response = await this.get(`projects/${projectId}/expenses/`, { 
        params: restParams 
      });
      return response.data;
    }
    
    // Fallback to generic endpoint
    const response = await this.get("expenses/", { params });
    return response.data;
  }

  async getDeletedExpenses(projectId, params = {}) {
    const response = await this.get(`projects/${projectId}/expenses/deleted/`, { params });
    return response.data;
  }

  async getExpense(id) {
    const response = await this.get(`expenses/${id}/`);
    return response.data;
  }

  async updateExpense(id, data) {
    const response = await this.patch(`expenses/${id}/`, data);
    return response.data;
  }

  async deleteExpense(id) {
    const response = await this.patch(`expenses/${id}/soft-delete/`);
    return response.data;
  }

  async restoreExpense(id) {
    const response = await this.patch(`expenses/${id}/restore/`);
    return response.data;
  }

  async getActivityExpenses(activityId, params = {}) {
    const response = await this.get(`activities/${activityId}/expenses/`, {
      params,
    });
    return response.data;
  }
}

// Service instances
export const apiService = new ApiService(); // Base API service
export const authService = new AuthService();
export const userService = new UserService();
export const documentService = new DocumentService();
export const projectService = new ProjectService();
export const outcomeService = new OutcomeService();
export const outputService = new OutputService();
export const activityService = new ActivityService();
export const expenseService = new ExpenseService();

// Service registry for easy access
export const services = {
  api: apiService,
  auth: authService,
  users: userService,
  documents: documentService,
  project: projectService,
  outcome: outcomeService,
  output: outputService,
  activity: activityService,
  expense: expenseService,
  indicator: indicatorService,
};

// Export default services object
export default services;
